/**
 * IMPORTANT! Module names correspond to permissions.
 */
export enum Module {
  AuditLogs = "audit-logs",
  Managers = "project-managers",
  Agreements = "agreements",
  ContestAgreements = "contest-agreements",
  Clients = "clients",
  Contractors = "contractors",
  ContractorStudents = "contractors-students",
  DefaultRates = "default-rates",
  DepartmentBasedAssignments = "department-based-assignments",
  DepartmentBasedProjectRates = "department-based-project-rates",
  ServicesAndGoods = "services-and-goods",
  InvoiceSpecification = "invoice-specification",
  PayrollDepartments = "payroll-departments",
  InvoicingCompanies = "invoicing-companies",
  Networks = "networks",
  Payrolls = "payrolls",
  PayrollTypes = "payroll-types",
  ProjectLimits = "project-limits",
  ProjectRoles = "project-roles",
  ProjectSchedules = "project-schedules",
  Projects = "projects",
  ProjectSettlements = "project-settlements",
  ProjectStores = "project-stores",
  ProjectStoresPeriods = "project-stores-periods",
  ProjectTypes = "project-types",
  Provinces = "provinces",
  RoleBasedAssignments = "role-based-assignments",
  ContractorVisits = "contractor-visits",
  ContractorVisitsContractors = "contractor-visits-contractors",
  ContractorVisitsSummary = "contractor-visits-summary",
  RoleBasedProjectRates = "role-based-project-rates",
  RolePermissions = "role-permissions",
  ShiftReportDepartments = "shift-report-departments",
  ShiftReports = "shift-reports",
  Shifts = "shifts",
  StoreFormats = "store-formats",
  StoreGroups = "store-groups",
  Stores = "stores",
  Surveys = "surveys",
  SurveyTemplates = "survey-templates",
  SystemPermissions = "system-permissions",
  SystemRoles = "system-roles",
  UpdatesDepartments = "updates-departments",
  UpdatesPerWeek = "updates-per-week",
  Users = "users",
  TimesheetEntries = "timesheet-entries",
  TimesheetEntriesShiftReports = "timesheet-entries-shift-reports",
  TimesheetEntriesVisits = "timesheet-entries-visits",
  TimesheetEntriesContractors = "timesheet-entries-contractors",
  WorkVisitsExport = "work-visits-export",
  StructureAssignments = "structure-assignments",
  AgreementCompanies = "agreement-companies",
  AgreementTypes = "agreement-types",
  TaxOffices = "tax-offices",
  Citizenships = "citizenships",
  DisabilityCertificates = "disability-certificates",
  DisabilityPensionTypes = "disability-pension-types",
  InvoiceSpecifications = "invoice-specifications",
  ContractorCards = "contractor-cards",
  DefaultStoreProjectAssignees = "default-store-project-assignees",
  StoreProjectAssignees = "store-project-assignees",
  ContractorPanelVisits = "contractor-panel-visits",
  ContractorPanelCards = "contractor-panel-cards",
  ContractorPanelPlannedHours = "contractor-panel-planned-hours",
  ContractorsPlannedHoursWizard = "contractors-planned-hours-wizard",
  SchedulesReport = "schedules-report",
  LimitsReport = "limits-report",
  ContractorsPlannedHoursApprovals = "contractors-planned-hours-approvals",
}
// =============================== CACHE CONSTANTS =============================== //
export const API_CACHE_NAME = "api-cache";
export const STATIC_ASSETS_CACHE_NAME = "static-assets";
export const PAGES_CACHE_NAME = "pages-cache";

// =============================== DATE CONSTANTS =============================== //
export const MIN_PROJECT_DATE = new Date(2025, 0, 1);

// =============================== NETWORK DETECTOR CONSTANTS =============================== //
export const DEFAULT_PING_INTERVAL = 30000;
export const DEFAULT_PING_TIMEOUT = 5000;

// =============================== PATHS START =============================== //
export const PATH_ROOT = "/";
export const PATH_ALL = "*";
export const PATH_PERMISSION_DENIED = "/permission-denied";
export const PATH_SIGN_IN = "/sign-in";
export const PATH_PASSWORD_RESET = "/password-reset";
export const PATH_PASSWORD_CHANGE = "/password-change";
export const PATH_PRODUCTS = "/products";
export const PATH_PRODUCT = `${PATH_PRODUCTS}/:productId`;
export const PATH_PRODUCT_GROUPS = "/product-groups";
export const PATH_PRODUCT_GROUP = `${PATH_PRODUCT_GROUPS}/:productGroupId`;

// TOP LEVEL MODULES PATHS
export const PATH_USER_PROFILE = `/user/profile`;
export const PATH_USERS = `/${Module.Users}`;
export const PATH_USER = `/${Module.Users}/:userId`;
export const PATH_AGREEMENTS = `/${Module.Agreements}`;
export const PATH_AGREEMENT = `/${Module.Agreements}/:agreementId`;
export const PATH_CLIENTS = `/${Module.Clients}`;
export const PATH_CLIENT = `/${Module.Clients}/:clientId`;
export const PATH_CONTRACTORS = `/${Module.Contractors}`;
export const PATH_CONTRACTORS_STUDENTS = `/${Module.ContractorStudents}`;
export const PATH_CONTRACTOR = `/${Module.Contractors}/:contractorId`;
export const PATH_TEMPLATES = `/${Module.SurveyTemplates}`;
export const PATH_SURVEYS = `/${Module.Surveys}`;
export const PATH_STORES = `/${Module.Stores}`;
export const PATH_CONTRACTOR_VISITS = `/${Module.ContractorPanelVisits}`;
export const PATH_CONTRACTOR_VISITS_EDIT = `/${Module.ContractorPanelVisits}/:contractorVisitId`;
export const PATH_CONTRACTOR_CARDS = `/${Module.ContractorPanelCards}`;
export const PATH_CONTRACTOR_CARD = `/${Module.ContractorPanelCards}/:contractorCardId`;
export const PATH_CONTRACTOR_PANEL_PLANNED_HOURS = `/${Module.ContractorPanelPlannedHours}`;
export const PATH_STORE = `/${Module.Stores}/:storeId`;
export const PATH_PROJECTS_SETTLEMENTS_DIRECTORY = `/${Module.ProjectSettlements}`;
export const PATH_PROJECTS_SETTLEMENTS_DIRECTORY_DETAILS = `${PATH_PROJECTS_SETTLEMENTS_DIRECTORY}/:settlementId`;
export const PATH_PROJECTS_SETTLEMENTS_DIRECTORY_INVOICE_SPECIFICATIONS_EDIT = `${PATH_PROJECTS_SETTLEMENTS_DIRECTORY_DETAILS}/invoice-specifications/:invoiceSpecificationId/edit`;
export const PATH_PROJECTS_SETTLEMENTS_DIRECTORY_INVOICE_SPECIFICATIONS_NEW = `${PATH_PROJECTS_SETTLEMENTS_DIRECTORY_DETAILS}/invoice-specifications/new`;
export const PATH_PROJECTS_ROLE_BASED_ASSIGNMENTS = `/${Module.RoleBasedAssignments}`;
export const PATH_PROJECTS_CONTRACTORS_PLANNED_HOURS_WIZARD = `/${Module.ContractorsPlannedHoursWizard}`;
export const PATH_DEFAULT_STORE_PROJECT_ASSIGNEES = `/${Module.DefaultStoreProjectAssignees}`;
export const PATH_DEFAULT_STORE_PROJECT_ASSIGNEES_NEW = `${PATH_DEFAULT_STORE_PROJECT_ASSIGNEES}/new`;
export const PATH_DEFAULT_STORE_PROJECT_ASSIGNEES_EDIT = `${PATH_DEFAULT_STORE_PROJECT_ASSIGNEES}/:assigneeId`;
export const PATH_STORE_PROJECT_ASSIGNEES = `/${Module.StoreProjectAssignees}`;
export const PATH_STORE_PROJECT_ASSIGNEES_NEW = `${PATH_STORE_PROJECT_ASSIGNEES}/new`;
export const PATH_STORE_PROJECT_ASSIGNEES_EDIT = `${PATH_STORE_PROJECT_ASSIGNEES}/:assigneeId`;
export const PATH_CONTEST_AGREEMENTS = `/${Module.ContestAgreements}`;
export const PATH_CONTEST_AGREEMENT = `/${Module.ContestAgreements}/:id`;
export const PATH_CONTEST_AGREEMENTS_LINK = `${PATH_CONTEST_AGREEMENTS}/link`;
export const PATH_AUDIT_LOGS = `/${Module.AuditLogs}`;
export const PATH_SCHEDULES_REPORT = `/${Module.SchedulesReport}`;
export const PATH_SENTRY_PLAYGROUND = "/sentry-playground";
export const PATH_LIMITS_REPORT = `/${Module.LimitsReport}`;
export const PATH_CONTRACTORS_PLANNED_HOURS_APPROVALS = `/${Module.ContractorsPlannedHoursApprovals}`;

// PROJECT MODULE PATHS
export const PATH_PROJECTS = `/${Module.Projects}`;
export const PATH_PROJECT_WITH_ID = `${PATH_PROJECTS}/:projectId`;
export const PATH_PROJECT_SETTLEMENTS = `${PATH_PROJECT_WITH_ID}/${Module.ProjectSettlements}`;
export const PATH_PROJECT_SETTLEMENTS_DETAILS = `${PATH_PROJECT_SETTLEMENTS}/:settlementId`;
export const PATH_PROJECT_INVOICE_SPECIFICATIONS_EDIT = `${PATH_PROJECT_SETTLEMENTS_DETAILS}/invoice-specifications/:invoiceSpecificationId/edit`;
export const PATH_PROJECT_INVOICE_SPECIFICATIONS_NEW = `${PATH_PROJECT_SETTLEMENTS_DETAILS}/invoice-specifications/new`;
export const PATH_PROJECTS_CONTRACTOR_CARDS = `${PATH_PROJECTS}/${Module.ContractorCards}`;
export const PATH_PROJECTS_CONTRACTOR_CARD = `${PATH_PROJECTS_CONTRACTOR_CARDS}/:contractorCardId`;

export const PATH_PROJECT_ROLES = `${PATH_PROJECT_WITH_ID}/${Module.ProjectRoles}`;
export const PATH_PROJECT_ROLE_NEW = `${PATH_PROJECT_ROLES}/new`;
export const PATH_PROJECT_ROLE_EDIT = `${PATH_PROJECT_ROLES}/:roleId/edit`;
export const PATH_PROJECT_ROLE_BASED_ASSIGNMENTS = `${PATH_PROJECT_WITH_ID}/${Module.RoleBasedAssignments}`;
export const PATH_PROJECT_DEPARTMENT_BASED_ASSIGNMENTS = `${PATH_PROJECTS}/${Module.DepartmentBasedAssignments}`;
export const PATH_PROJECT_LIMITS = `${PATH_PROJECT_WITH_ID}/${Module.ProjectLimits}`;
export const PATH_PROJECT_SCHEDULES = `${PATH_PROJECT_WITH_ID}/${Module.ProjectSchedules}`;
export const PATH_PROJECT_STORES = `${PATH_PROJECT_WITH_ID}/${Module.ProjectStores}`;
export const PATH_PROJECT_RATES = `${PATH_PROJECT_WITH_ID}/${Module.RoleBasedProjectRates}`;
export const PATH_PROJECT_RATE_NEW = `${PATH_PROJECT_RATES}/new`;
export const PATH_PROJECT_RATE_EDIT = `${PATH_PROJECT_RATES}/:rateId/edit`;

// INVOICE SPECIFICATIONS MODULE PATHS
export const PATH_INVOICE_SPECIFICATIONS = `/${Module.InvoiceSpecifications}`;
export const PATH_INVOICE_SPECIFICATIONS_DETAILS = `${PATH_INVOICE_SPECIFICATIONS}/:invoiceSpecificationId`;

// SHIFT REPORTS MODULE PATHS
export const PATH_SHIFT_REPORTS = `/${Module.ShiftReports}`;
export const PATH_SHIFT_REPORT = `${PATH_SHIFT_REPORTS}/:shiftReportId`;

// DICTIONARIES MODULE PATHS
export const PATH_DICTS = "/dictionaries";

export const PATH_DICTS_PROVINCES = `${PATH_DICTS}/${Module.Provinces}`;
export const PATH_DICTS_STORE_FORMATS = `${PATH_DICTS}/${Module.StoreFormats}`;
export const PATH_DICTS_PROJECT_TYPES = `${PATH_DICTS}/${Module.ProjectTypes}`;
export const PATH_DICTS_SHIFTS = `${PATH_DICTS}/${Module.Shifts}`;
export const PATH_DICTS_SHIFT_REPORT_DEPARTMENTS = `${PATH_DICTS}/${Module.ShiftReportDepartments}`;
export const PATH_DICTS_NETWORKS = `${PATH_DICTS}/${Module.Networks}`;
export const PATH_DICTS_PAYROLL_DEPARTMENTS = `${PATH_DICTS}/${Module.PayrollDepartments}`;
export const PATH_DICTS_DEPARTMENT_BASED_PROJECT_RATES = `${PATH_DICTS}/${Module.DepartmentBasedProjectRates}`;
export const PATH_DICTS_UPDATES_DEPARTMENTS = `${PATH_DICTS}/${Module.UpdatesDepartments}`;
export const PATH_DICTS_STORE_GROUPS = `${PATH_DICTS}/${Module.StoreGroups}`;
export const PATH_DICTS_SERVICES_AND_GOODS = `${PATH_DICTS}/${Module.ServicesAndGoods}`;
export const PATH_DICTS_PAYROLL_TYPES = `${PATH_DICTS}/${Module.PayrollTypes}`;
export const PATH_DICTS_AGREEMENT_COMPANIES = `${PATH_DICTS}/${Module.AgreementCompanies}`;
export const PATH_DICTS_AGREEMENT_TYPES = `${PATH_DICTS}/${Module.AgreementTypes}`;
export const PATH_DICTS_TAX_OFFICES = `${PATH_DICTS}/${Module.TaxOffices}`;
export const PATH_DICTS_CITIZENSHIPS = `${PATH_DICTS}/${Module.Citizenships}`;
export const PATH_DICTS_DISABILITY_CERTIFICATES = `${PATH_DICTS}/${Module.DisabilityCertificates}`;

// UPDATES PER WEEK MODULE PATHS
export const PATH_UPDATES = `/${Module.UpdatesPerWeek}`;

// TIMESHEET ENTRIES MODULE PATHS
export const PATH_TIMESHEET_ENTRIES = `/${Module.TimesheetEntries}`;
export const PATH_TIMESHEET_ENTRIES_CONTRACTOR_VISITS = `${PATH_TIMESHEET_ENTRIES}/:shiftReportId?/visits/:userId`;
export const PATH_TIMESHEET_ENTRIES_SHIFT_REPORTS = `${PATH_TIMESHEET_ENTRIES}/:shiftReportId`;
export const PATH_TIMESHEET_ENTRIES_CONTRACTORS = `${PATH_TIMESHEET_ENTRIES}/:shiftReportId/contractors`;
export const PATH_PROJECT_CONTRACTOR_VISITS = `${PATH_PROJECTS}/${Module.ContractorVisits}`;
export const PATH_PROJECT_CONTRACTOR_VISIT = `${PATH_PROJECT_CONTRACTOR_VISITS}/:contractorVisitId`;
export const PATH_PROJECT_CONTRACTOR_VISITS_EDITOR_DIRECTORY = `${PATH_PROJECTS}/:projectId/${Module.ContractorVisits}`;
export const PATH_PROJECT_CONTRACTOR_VISIT_NEW = `${PATH_PROJECT_CONTRACTOR_VISITS}/new`;
export const PATH_PROJECT_MANAGERS = `${PATH_PROJECTS}/:projectId/${Module.Managers}`;
export const PATH_PROJECT_MANAGER_EDIT = `${PATH_PROJECT_MANAGERS}/:managerId/edit`;

// STRUCTURE ASSIGNMENTS MODULE PATHS
export const PATH_STRUCTURE_ASSIGNMENTS = `/${Module.StructureAssignments}/:assignmentType`;
export const PATH_STRUCTURE_ASSIGNMENT = `/${Module.StructureAssignments}/:assignmentType/:supervisorId`;
export const PATH_STRUCTURE_ASSIGNMENT_NEW = `/${Module.StructureAssignments}/new`;

// PAYROLLS MODULE PATHS
export const PATH_PAYROLLS = `/${Module.Payrolls}`;
export const PATH_PAYROLL = `${PATH_PAYROLLS}/:payrollId`;
export const PATH_PAYROLL_PREVIEW = `${PATH_PAYROLLS}/:payrollId/preview`;
export const PATH_PAYROLL_AGGREGATION = `${PATH_PAYROLLS}/:payrollId/aggregation`;
export const PATH_PAYROLL_INSPECTION = `${PATH_PAYROLLS}/:payrollId/inspection`;
export const PATH_PAYROLL_SPLIT = `${PATH_PAYROLLS}/:payrollId/split`;
export const PATH_PAYROLL_INSPECTION_DETAIL = `${PATH_PAYROLL_INSPECTION}/:entryId`;
export const PATH_PAYROLL_AGGREGATION_DETAIL = `${PATH_PAYROLL_AGGREGATION}/:entryId`;
export const PATH_PAYROLL_SPLIT_DETAIL = `${PATH_PAYROLL_SPLIT}/:entryId`;

// PUBLIC PATHS
export const PATH_PUBLIC_QUESTIONNAIRE = "/agreements/questionnaire-link";

// =============================== PATHS END =============================== //

// Used as a placeholder in the project name in surveys
export const PRODUCT_PLACEHOLDER = "%PRODUKT%";

export const DEFAULT_LAT = 52.237049;
export const DEFAULT_LNG = 21.017532;

/** The pattern matches the following variations:
 * - "id"
 * - "testId"
 * - "test_id"
 * - "test_Id"
 * Used to find Id in schema keys.
 */
export const ID_REGEX = /^id$|_id$|_Id$|Id$/;
export const POSTAL_CODE_REGEX = /^\d{2}-\d{3}$/;
export const NIP_REGEXP = /^(\d{10}|\d{3}-\d{3}-\d{2}-\d{2})$/;
export const PESEL_REGEX = /^\d{11}$/;
export const BANK_ACCOUNT_NUMBER_REGEX = /^\d{26}$/;
export const LATITUDE_REGEX = /^(-?(90(\.0+)?|[0-8]?\d(\.\d+)?))$/;
export const LONGITUDE_REGEX =
  /^(-?(180(\.0+)?|1[0-7]?\d(\.\d+)?|[0-9]?\d(\.\d+)?)?)$/;

export const DEFAULT_DATE_FORMAT = "yyyy-MM-dd";
export const YM_DATE_FORMAT = "yyyy-MM";
export const TIME_FORMAT = "HH:mm";
export const DATETIME_FORMAT = "yyyy-MM-dd HH:mm";
export const DATETIME_FORMAT_WITH_SECONDS = "yyyy-MM-dd HH:mm:ss";
export const ISO_DATETIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ss'Z'";
export const LABEL_DAY_MONTH = "d LLL";
export const LABEL_DAY_MONTH_YEAR = "d LLL yyyy";

export const DEFAULT_FRACTION_DIGITS = 2;

export const DEFAULT_COLUMN_SIZE = 200;
export const DEFAULT_COLUMN_MIN_SIZE = 132;

export const DEFAULT_ROW_HEIGHT = 33;
export const DEFAULT_SELECT_COLUMN_SIZE = 23;

export const DAYS_IN_WEEK = 7;
export const WEEK_START = 1;

export const NO_DATA = "--";
export const NO_TIME = "--:--";

export const BOOLEAN_VALUES = ["true", "false"] as const;

export const FILE_PREVIEW_SUPPORTED_EXTENSIONS = [
  "pdf",
  "jpg",
  "jpeg",
  "png",
] as const;

export const MIME_EXTENSION_MAP = {
  "application/pdf": ".pdf",
  "image/jpeg": ".jpg",
  "image/png": ".png",
} as const;

export const MIME_TO_EXTENSION = Object.fromEntries(
  Object.entries(MIME_EXTENSION_MAP).map(([mime, ext]) => [mime, ext.slice(1)]),
) as Record<string, string>;

export const EXTENSION_TO_FILE_PREVIEW_TYPE: Record<string, string> = {
  pdf: "pdf",
  jpg: "jpg",
  jpeg: "jpg",
  png: "png",
};

export const ALLOWED_EXTENSIONS = Object.values(MIME_EXTENSION_MAP);
export const ALLOWED_MIME_TYPES = Object.keys(MIME_EXTENSION_MAP) as string[];
export const ACCEPT_EXTENSIONS_STRING = FILE_PREVIEW_SUPPORTED_EXTENSIONS.map(
  (ext) => `.${ext}`,
).join(",");
export const ALLOWED_TYPES_DISPLAY = FILE_PREVIEW_SUPPORTED_EXTENSIONS.map(
  (ext) => ext.toUpperCase(),
).join(", ");

export const CONTRACTOR_CARD_FILE_INPUT_ID = "contractor-card-file-input";

export const NEW_WINDOW_DEAFULT_WIDTH = 800;
export const NEW_WINDOW_DEAFULT_HEIGHT = 600;

export const FILE_PREVIEW_DEFAULT_MAX_WIDTH = 950;
export const FILE_PREVIEW_DEFAULT_MAX_HEIGHT = 800;
export const FILE_PREVIEW_MAX_PDF_WIDTH = 900;
export const FILE_PREVIEW_DESKTOP_PADDING = 32;
export const FILE_PREVIEW_MOBILE_PADDING = 0;
export const FILE_PREVIEW_MIN_HEIGHT = 400;
export const FILE_PREVIEW_MIN_UNSUPPORTED_HEIGHT = 200;
export const FILE_PREVIEW_IMAGE_MAX_WIDTH_DESKTOP = 800;
export const FILE_PREVIEW_IMAGE_MAX_WIDTH_MOBILE = 400;
export const FILE_PREVIEW_IMAGE_MAX_HEIGHT_DESKTOP = 800;
export const FILE_PREVIEW_IMAGE_MAX_HEIGHT_MOBILE = 600;
export const MAX_FILENAME_DISPLAY_LENGTH = 250;

export const ATTACHMENT_MAX_FILES_AT_ONCE = 10;
export const ATTACHMENT_MAX_FILE_SIZE_MB = 10;
export const ATTACHMENT_MAX_FILE_SIZE_BYTES =
  ATTACHMENT_MAX_FILE_SIZE_MB * 1024 * 1024;
export const ATTACHMENT_CONCURRENT_UPLOADS = 3;

export const AGREEMENT_DOCUMENTS_ATTACHMENT_TYPE = "agreement" as const;

export const QUESTIONNAIRE_INSTRUCTIONAL_VIDEO_URL =
  "https://www.youtube.com/embed/0S-8T_HHe34";

export const SERVICE_WORKER_UPDATE_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

// Export formats
export const EXPORT_FORMAT_CSV = "csv";
export const EXPORT_FORMAT_XLSX = "xlsx";

export const COLUMN_SELECT_ID = "select";

export const PL_COUNTRY_CODE = "PL";

export const HOURS_WORKED_CORRECTED_MAX = 9999.999;
